import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { UtensilsCrossed, Search, BookMarked, Bookmark, ChevronLeft, Plus, Trash2, Leaf, X, Map, Sparkles, ArrowRight } from "lucide-react";
import CuisineMap from "@/modules/CuisineMap";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgUrl = (r) => (r.image_url ? IMG_BASE + r.image_url : null);

const TYPE_STYLE = {
  "Traditional": "bg-[#E7F6EC] text-[#166534]",
  "Historically Documented": "bg-[#FEF3C7] text-[#92400E]",
  "Contemporary Tribal": "bg-[#EDE9FE] text-[#5B21B6]",
};

const field = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";

function RecipeDetail({ r, saved, onBack, onToggleSave, onNavigate }) {
  return (
    <div data-testid="recipe-detail">
      <button onClick={onBack} data-testid="recipe-back-btn" className="inline-flex items-center gap-1.5 text-sm text-[#59288E] font-semibold mb-4 hover:underline"><ChevronLeft size={16} /> Back to recipes</button>
      <Panel className="p-6">
        {imgUrl(r) ? <img src={imgUrl(r)} alt={r.name} data-testid="recipe-detail-image" className="w-full h-64 object-cover rounded-xl mb-4" /> : null}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TYPE_STYLE[r.recipe_type] || "bg-[#F3EEFA] text-[#59288E]"}`}>{r.recipe_type}</span>
            <h2 className="font-serif text-3xl font-bold text-[#180D24] mt-2">{r.name}</h2>
            <div className="text-sm text-[#8F7220] font-mono mt-1">{r.tribe} · {r.region} · {r.dish_type}</div>
          </div>
          <button onClick={() => onToggleSave(r)} data-testid="recipe-save-btn"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${saved ? "bg-[#166534] text-white" : "bg-[#D4AF37] text-[#180D24] hover:bg-[#E7C965]"}`}>
            {saved ? <BookMarked size={15} /> : <Bookmark size={15} />} {saved ? "In my cookbook" : "Save to my cookbook"}
          </button>
        </div>
        <h3 className="font-serif text-lg font-semibold text-[#59288E] mt-4 mb-1">About</h3>
        <p className="text-[#1A171D] leading-relaxed">{r.about}</p>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <h3 className="font-serif text-lg font-semibold text-[#59288E] mb-2">Ingredients</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm text-[#1A171D]">{r.ingredients.map((i, k) => <li key={k}>{i}</li>)}</ul>
          </div>
          <div>
            <h3 className="font-serif text-lg font-semibold text-[#59288E] mb-2">Preparation</h3>
            <ol className="list-decimal pl-5 space-y-1 text-sm text-[#1A171D]">{r.preparation.map((s, k) => <li key={k}>{s}</li>)}</ol>
          </div>
        </div>

        {r.traditional_method ? <Section title="Traditional method">{r.traditional_method}</Section> : null}
        {r.historical_ingredients?.length ? <Section title="Historical ingredients">{r.historical_ingredients.join(" · ")}</Section> : null}
        {r.modern_adaptations ? <Section title="Modern adaptations">{r.modern_adaptations}</Section> : null}

        <div className="mt-5 rounded-xl bg-[#FBF7FF] border border-[#59288E]/15 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-[#8F7220] mb-1">Evidence status</div>
          <div className="text-sm font-semibold text-[#3F1E68]">{r.evidence_status}</div>
          <div className="text-xs font-bold uppercase tracking-wide text-[#8F7220] mt-3 mb-1">Sources</div>
          <ul className="space-y-1">{r.sources.map((s, k) => <li key={k} className="text-sm text-[#1A171D]"><span className="font-semibold">{s.title}</span> — {s.detail}</li>)}</ul>
        </div>
        {r.related_plants?.length ? (
          <div className="mt-4 text-sm text-[#59288E] flex items-center gap-2 flex-wrap"><Leaf size={15} /> Connected plant knowledge:
            {r.related_plants.map((pl, k) => (
              <button key={k} data-testid={`recipe-plant-link-${k}`} onClick={() => onNavigate && onNavigate("tribal_remedies", { search: pl })}
                className="rounded-full bg-[#E7F6EC] text-[#166534] px-2.5 py-0.5 text-xs font-semibold hover:bg-[#166534] hover:text-white transition-colors">
                {pl} →
              </button>
            ))}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="font-serif text-base font-semibold text-[#180D24]">{title}</h3>
    <p className="text-sm text-[#1A171D] leading-relaxed mt-1">{children}</p>
  </div>
);

function FeaturedHero({ recipe, onView }) {
  if (!recipe) return null;
  return (
    <button onClick={() => onView(recipe)} data-testid="featured-dish-hero"
      className="group relative block w-full overflow-hidden rounded-3xl mb-6 text-left focus:outline-none focus:ring-2 focus:ring-[#D4AF37]">
      <img src={imgUrl(recipe)} alt={recipe.name}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#180D24]/90 via-[#180D24]/65 to-transparent" />
      <div className="relative z-10 p-7 sm:p-10 max-w-2xl min-h-[240px] flex flex-col justify-end">
        <div className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#D4AF37] text-[#180D24] px-3 py-1 text-[11px] font-bold uppercase tracking-wide mb-3">
          <Sparkles size={13} /> Featured dish
        </div>
        <h3 data-testid="featured-dish-name" className="font-serif text-3xl sm:text-4xl font-bold text-white leading-tight">{recipe.name}</h3>
        <div className="text-sm text-[#E7C965] font-mono mt-1.5">{recipe.tribe} · {recipe.region}</div>
        <p className="text-sm text-[#E9E2F2] mt-2 line-clamp-2">{recipe.about}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:gap-2.5 transition-all">
          View recipe <ArrowRight size={16} />
        </span>
      </div>
    </button>
  );
}

function FamilyRecipeForm({ onCreated }) {
  const [f, setF] = useState({ name: "", taught_by: "", family_line: "", origin_place: "", ingredients: "", instructions: "", story: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Please give the recipe a name."); return; }
    setBusy(true);
    try { const { data } = await api.post("/family-recipes", f); onCreated(data); setF({ name: "", taught_by: "", family_line: "", origin_place: "", ingredients: "", instructions: "", story: "" }); toast.success("Saved to your family archive"); }
    catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-5" data-testid="family-recipe-form">
      <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-1">Add a family recipe</h3>
      <p className="text-xs text-[#756D7E] mb-3">Your family is part of the story too. Family-submitted recipes are labeled <span className="font-semibold">FAMILY RECIPE</span> — never attributed to a Nation automatically.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Recipe name" value={f.name} onChange={set("name")} data-testid="family-name-input" />
        <input className={field} placeholder="Who taught you this recipe?" value={f.taught_by} onChange={set("taught_by")} />
        <input className={field} placeholder="Family line" value={f.family_line} onChange={set("family_line")} />
        <input className={field} placeholder="Where was this person from?" value={f.origin_place} onChange={set("origin_place")} />
      </div>
      <textarea className={`${field} mt-3`} rows={2} placeholder="Ingredients" value={f.ingredients} onChange={set("ingredients")} />
      <textarea className={`${field} mt-3`} rows={2} placeholder="Instructions" value={f.instructions} onChange={set("instructions")} />
      <textarea className={`${field} mt-3`} rows={2} placeholder="Tell the story behind this recipe" value={f.story} onChange={set("story")} />
      <button onClick={submit} disabled={busy} data-testid="family-save-btn" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#482276] transition-colors disabled:opacity-60"><Plus size={15} /> Save to my family archive</button>
    </Panel>
  );
}

export default function TribalRecipes({ onNavigate, navPayload }) {
  const [data, setData] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [family, setFamily] = useState([]);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [tribe, setTribe] = useState("");
  const [region, setRegion] = useState("");
  const [type, setType] = useState("");
  const [tab, setTab] = useState("browse");

  const load = () => api.get("/recipes").then((r) => { setData(r.data); setSavedIds(r.data.saved_ids); });
  const loadFamily = () => api.get("/family-recipes").then((r) => setFamily(r.data.items));
  useEffect(() => { load(); loadFamily(); }, []);

  const toggleSave = async (r) => {
    const { data: res } = await api.post(`/recipes/${r.id}/save`);
    setSavedIds((s) => res.saved ? [...s, r.id] : s.filter((x) => x !== r.id));
    toast.success(res.saved ? "Saved to your cookbook" : "Removed from cookbook");
  };

  const featured = useMemo(() => {
    if (!data) return null;
    const withPhoto = data.recipes.filter((r) => r.image_url);
    if (!withPhoto.length) return null;
    return withPhoto[Math.floor(Math.random() * withPhoto.length)];
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.recipes.filter((r) => {
      if (tribe && r.tribe !== tribe) return false;
      if (region && r.region !== region) return false;
      if (type && r.recipe_type !== type) return false;
      if (q) {
        const hay = `${r.name} ${r.tribe} ${r.main_ingredients.join(" ")} ${r.about}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, tribe, region, type, q]);

  useEffect(() => {
    if (!data || !navPayload) return;
    if (navPayload.recipeId) {
      const r = data.recipes.find((x) => x.id === navPayload.recipeId);
      if (r) { setDetail(r); setTab("browse"); }
    } else if (navPayload.search) { setQ(navPayload.search); setTab("browse"); }
  }, [data, navPayload]);

  if (!data) return <Loader />;
  if (detail) return <RecipeDetail r={detail} saved={savedIds.includes(detail.id)} onBack={() => setDetail(null)} onToggleSave={toggleSave} onNavigate={onNavigate} />;

  const savedRecipes = data.recipes.filter((r) => savedIds.includes(r.id));

  return (
    <div data-testid="module-tribal-recipes">
      <PageHeader eyebrow="Tribal Recipes" title="Tribal Recipes"
        subtitle="Taste the history, learn the culture. Explore traditional and historically documented Indigenous foods — each with its Nation, evidence status and sources." />

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[["browse", "Explore recipes"], ["map", "Cuisine Map"], ["cookbook", `My cookbook (${savedRecipes.length + family.length})`], ["family", "Add family recipe"]].map(([id, label]) => (
          <button key={id} data-testid={`recipes-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-[#2A1442] text-white" : "bg-white border border-[#E4DCD3] text-[#59288E] hover:border-[#D4AF37]"}`}>{label}</button>
        ))}
      </div>

      {tab === "map" && <div data-testid="recipes-map-tab"><CuisineMap onNavigate={onNavigate || (() => {})} /></div>}

      {tab === "browse" && (
        <>
          <FeaturedHero recipe={featured} onView={setDetail} />
          <Panel className="p-4 mb-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756D7E]" />
                <input className={`${field} pl-9`} placeholder="Search dish or ingredient…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="recipe-search-input" />
              </div>
              <select className={field} value={tribe} onChange={(e) => setTribe(e.target.value)} data-testid="recipe-tribe-filter"><option value="">All Nations</option>{data.meta.tribes.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select className={field} value={region} onChange={(e) => setRegion(e.target.value)}><option value="">All Regions</option>{data.meta.regions.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select className={field} value={type} onChange={(e) => setType(e.target.value)}><option value="">All Types</option>{data.meta.recipe_types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
            </div>
          </Panel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipe-grid">
            {filtered.map((r) => (
              <button key={r.id} onClick={() => setDetail(r)} data-testid={`recipe-card-${r.id}`} className="text-left rounded-2xl border border-[#E4DCD3] bg-white overflow-hidden hover:border-[#D4AF37] hover:shadow-md transition-all">
                {imgUrl(r) ? <img src={imgUrl(r)} alt={r.name} className="w-full h-40 object-cover" /> : null}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TYPE_STYLE[r.recipe_type] || ""}`}>{r.recipe_type}</span>
                    {savedIds.includes(r.id) ? <BookMarked size={15} className="text-[#166534]" /> : null}
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#180D24] mt-2">{r.name}</h3>
                  <div className="text-xs text-[#8F7220] font-mono mt-1">{r.tribe}</div>
                  <p className="text-sm text-[#756D7E] mt-2 line-clamp-3">{r.about}</p>
                </div>
              </button>
            ))}
            {filtered.length === 0 ? <div className="col-span-full text-center py-12 text-[#756D7E]">No recipes match those filters.</div> : null}
          </div>
        </>
      )}

      {tab === "cookbook" && (
        <div data-testid="cookbook-view">
          {savedRecipes.length + family.length === 0 ? <div className="text-center py-14 text-[#756D7E]">Your cookbook is empty. Save recipes or add a family recipe.</div> : null}
          {savedRecipes.length ? <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-3">Saved recipes</h3> : null}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {savedRecipes.map((r) => (
              <button key={r.id} onClick={() => setDetail(r)} className="text-left rounded-2xl border border-[#E4DCD3] bg-white p-5 hover:border-[#D4AF37] transition-all">
                <h3 className="font-serif text-lg font-semibold text-[#180D24]">{r.name}</h3>
                <div className="text-xs text-[#8F7220] font-mono mt-1">{r.tribe}</div>
              </button>
            ))}
          </div>
          {family.length ? <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-3">My family recipes</h3> : null}
          <div className="space-y-3">
            {family.map((r) => (
              <div key={r.id} className="rounded-2xl border border-[#E4DCD3] bg-white p-5" data-testid={`family-recipe-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">Family recipe</span>
                    <h4 className="font-serif text-xl font-semibold text-[#180D24] mt-1">{r.name}</h4>
                    {r.taught_by ? <div className="text-xs text-[#8F7220] mt-0.5">Taught by {r.taught_by}{r.family_line ? ` · ${r.family_line}` : ""}</div> : null}
                  </div>
                  <button onClick={async () => { await api.delete(`/family-recipes/${r.id}`); setFamily((s) => s.filter((x) => x.id !== r.id)); }} className="p-1.5 rounded-full border border-[#E4DCD3] text-[#B91C1C] hover:bg-[#FEE2E2]"><Trash2 size={14} /></button>
                </div>
                {r.ingredients ? <p className="text-sm text-[#1A171D] mt-2"><span className="font-semibold">Ingredients:</span> {r.ingredients}</p> : null}
                {r.instructions ? <p className="text-sm text-[#1A171D] mt-1"><span className="font-semibold">Instructions:</span> {r.instructions}</p> : null}
                {r.story ? <p className="text-sm text-[#756D7E] italic mt-2">“{r.story}”</p> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "family" && <FamilyRecipeForm onCreated={(d) => { setFamily((s) => [d, ...s]); setTab("cookbook"); }} />}
    </div>
  );
}
